import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProdutoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    category: 'sementes',
    image_url: '',
    description: '',
    classe_agronomica: '',
    principio_ativo: '',
    formulacao: '',
    grupo_quimico: '',
    modo_acao: '',
    indicacoes_uso: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    { value: 'sementes', label: 'Sementes' },
    { value: 'defensivos', label: 'Defensivos' },
    { value: 'fertilizantes', label: 'Fertilizantes' },
    { value: 'agricultura_precisao', label: 'Agricultura de precisão' },
  ];

  useEffect(() => {
    if (isEditing) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Erro ao buscar produto:', error);
      setError('Produto não encontrado');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditing) {
        await axios.put(`${API}/products/${id}`, formData);
        alert('Produto atualizado com sucesso!');
      } else {
        await axios.post(`${API}/products`, formData);
        alert('Produto criado com sucesso!');
      }
      navigate('/admin/produtos');
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      setError('Erro ao salvar produto. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <Link to="/admin/produtos" className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditing ? 'Editar Produto' : 'Novo Produto'}
            </h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-8">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nome */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Nome do Produto*</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="Ex: PREN-D 806"
              />
            </div>

            {/* Categoria */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Categoria*</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* URL da Imagem */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">URL da Imagem</label>
              <input
                type="url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="https://exemplo.com/imagem.jpg"
              />
              {formData.image_url && (
                <div className="mt-2">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Descrição</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="Descrição detalhada do produto"
              ></textarea>
            </div>

            {/* Classe Agronômica */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Classe Agronômica</label>
              <input
                type="text"
                name="classe_agronomica"
                value={formData.classe_agronomica}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="Ex: Herbicida"
              />
            </div>

            {/* Princípio Ativo */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Princípio Ativo</label>
              <input
                type="text"
                name="principio_ativo"
                value={formData.principio_ativo}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="Ex: Glifosato"
              />
            </div>

            {/* Formulação */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Formulação</label>
              <input
                type="text"
                name="formulacao"
                value={formData.formulacao}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="Ex: Concentrado Solúvel"
              />
            </div>

            {/* Grupo Químico */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Grupo Químico</label>
              <input
                type="text"
                name="grupo_quimico"
                value={formData.grupo_quimico}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="Ex: Glicina"
              />
            </div>

            {/* Modo de Ação */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Modo de Ação</label>
              <input
                type="text"
                name="modo_acao"
                value={formData.modo_acao}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="Ex: Sistêmico"
              />
            </div>

            {/* Indicações de Uso */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Indicações de Uso</label>
              <textarea
                name="indicacoes_uso"
                value={formData.indicacoes_uso}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                placeholder="Indicações e recomendações de uso"
              ></textarea>
            </div>

            {/* Buttons */}
            <div className="flex items-center space-x-4 pt-6">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar Produto'}</span>
              </button>
              <Link
                to="/admin/produtos"
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Cancelar
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminProdutoForm;